import * as React from 'react';
import { Command as CommandPrimitive } from 'cmdk';
import { Search } from 'lucide-react';

import { cn } from 'helpers/css';
import { Dialog, DialogContent } from 'shadcn/components/dialog';

const Command = React.forwardRef(({ className, ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    className={cn(
      'tw-flex tw-h-full tw-w-full tw-flex-col tw-overflow-hidden tw-rounded-md tw-bg-popover dark:tw-bg-[var(--bg-dark-input)] tw-text-popover-foreground',
      className
    )}
    {...props}
  />
));
Command.displayName = CommandPrimitive.displayName;

const CommandDialog = ({ children, ...props }) => {
  return (
    <Dialog {...props}>
      <DialogContent className='tw-overflow-hidden tw-p-0'>
        <Command className='[&_[cmdk-group-heading]]:tw-px-2 [&_[cmdk-group-heading]]:tw-font-medium [&_[cmdk-group-heading]]:tw-text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:tw-pt-0 [&_[cmdk-group]]:tw-px-2 [&_[cmdk-input-wrapper]_svg]:tw-h-5 [&_[cmdk-input-wrapper]_svg]:tw-w-5 [&_[cmdk-input]]:tw-h-12 [&_[cmdk-item]]:tw-px-2 [&_[cmdk-item]]:tw-py-3 [&_[cmdk-item]_svg]:tw-h-5 [&_[cmdk-item]_svg]:tw-w-5'>
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
};

const CommandInput = React.forwardRef(
  ({ className, onMouseDown, onTouchStart, onKeyDown, onBlur, ...props }, ref) => {
    // Safari / iCloud Keychain treats a bare text input as a possible login field and offers
    // password autofill on every filter search box (Progress, Message type, Routes …). A
    // `search`-typed input is never a credential field, so the keychain stays away. cmdk pins
    // type="text" after spreading props, so the type is switched on the DOM node instead;
    // React never rewrites it because the prop value does not change. The data-* attributes
    // opt out of 1Password/LastPass. (TEX-272, same class as TEX-257.)
    const setSearchType = React.useCallback(
      (node) => {
        if (node) node.type = 'search';
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref]
    );
    // TEX-332: type=search + autocomplete=off were not enough for the iCloud Passwords popover
    // (Safari 18, Group By in Click Analytics). Password managers decide at FOCUS time and skip
    // read-only fields, so the box is read-only until the user actually interacts with it —
    // pointer down or a key press lifts the guard synchronously, before the key's default
    // action inserts the character, so nothing typed is lost. Blur re-arms it for the next open.
    const [guarded, setGuarded] = React.useState(true);
    const disarm = React.useCallback((node) => {
      if (node) node.readOnly = false;
      setGuarded(false);
    }, []);
    return (
      <div className='tw-flex tw-items-center tw-border-b tw-px-3' cmdk-input-wrapper=''>
        <Search className='tw-mr-2 tw-h-4 tw-w-4 tw-shrink-0 tw-opacity-50' />
        <CommandPrimitive.Input
          ref={setSearchType}
          readOnly={guarded}
          data-1p-ignore=''
          data-lpignore='true'
          data-form-type='other'
          onMouseDown={(e) => {
            disarm(e.currentTarget);
            onMouseDown && onMouseDown(e);
          }}
          onTouchStart={(e) => {
            disarm(e.currentTarget);
            onTouchStart && onTouchStart(e);
          }}
          onKeyDown={(e) => {
            disarm(e.currentTarget);
            onKeyDown && onKeyDown(e);
          }}
          onBlur={(e) => {
            setGuarded(true);
            onBlur && onBlur(e);
          }}
          className={cn(
            'tw-flex tw-h-10 tw-w-full tw-rounded-md tw-bg-transparent tw-py-3 tw-text-sm tw-outline-none tw-appearance-none [&::-webkit-search-cancel-button]:tw-hidden [&::-webkit-search-decoration]:tw-hidden placeholder:tw-text-muted-foreground disabled:tw-cursor-not-allowed disabled:tw-opacity-50',
            className
          )}
          {...props}
        />
      </div>
    );
  }
);

CommandInput.displayName = CommandPrimitive.Input.displayName;

const CommandList = React.forwardRef(({ className, ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    className={cn('tw-max-h-[300px] tw-overflow-y-auto tw-overflow-x-hidden', className)}
    {...props}
  />
));

CommandList.displayName = CommandPrimitive.List.displayName;

const CommandEmpty = React.forwardRef((props, ref) => (
  <CommandPrimitive.Empty ref={ref} className='tw-py-6 tw-text-center tw-text-sm' {...props} />
));

CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const CommandGroup = React.forwardRef(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      'tw-overflow-hidden tw-p-1 tw-text-foreground [&_[cmdk-group-heading]]:tw-px-2 [&_[cmdk-group-heading]]:tw-py-1.5 [&_[cmdk-group-heading]]:tw-text-xs [&_[cmdk-group-heading]]:tw-font-medium [&_[cmdk-group-heading]]:tw-text-muted-foreground',
      className
    )}
    {...props}
  />
));

CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CommandSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={cn('tw--mx-1 tw-h-px tw-bg-border', className)}
    {...props}
  />
));
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

const CommandItem = React.forwardRef(({ className, ...props }, ref) => (
  <CommandPrimitive.Item
    ref={ref}
    className={cn(
      'tw-relative tw-flex tw-cursor-default tw-gap-2 tw-select-none tw-items-center tw-rounded-sm tw-px-2 tw-py-1.5 tw-text-sm tw-outline-none data-[disabled=true]:tw-pointer-events-none hover:tw-bg-accent data-[selected=true]:tw-text-accent-foreground data-[disabled=true]:tw-opacity-50 [&_svg]:tw-pointer-events-none [&_svg]:tw-size-4 [&_svg]:tw-shrink-0',
      className
    )}
    {...props}
  />
));

CommandItem.displayName = CommandPrimitive.Item.displayName;

const CommandShortcut = ({ className, ...props }) => {
  return (
    <span
      className={cn('tw-ml-auto tw-text-xs tw-tracking-widest tw-text-muted-foreground', className)}
      {...props}
    />
  );
};
CommandShortcut.displayName = 'CommandShortcut';

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
